import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import CourseCard from "../components/CourseCard";
import RecentlyViewed from "../components/RecentlyViewed";
import { useSEO } from "../lib/seo";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "../components/ui/button";

const VENDORS = ["Amazon Web Services", "Microsoft", "Google", "CNCF", "Cisco", "CompTIA", "PMI"];
const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced", "Expert"];

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const vendor = params.get("vendor") || "";
  const difficulty = params.get("difficulty") || "";
  const sort = params.get("sort") || "popular";

  const fetchCourses = useMemo(() => async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    if (vendor) qs.set("vendor", vendor);
    if (difficulty) qs.set("difficulty", difficulty);
    if (sort) qs.set("sort", sort);
    try {
      const { data } = await api.get(`/courses?${qs.toString()}`);
      setCourses(data);
    } finally {
      setLoading(false);
    }
  }, [q, category, vendor, difficulty, sort]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  useSEO({
    title: category
      ? `${category} practice tests`
      : q
      ? `Search: ${q}`
      : "All certification practice tests",
    description: `Browse ${courses.length}+ certification practice tests${category ? ` for ${category}` : ""}. Updated for 2026 with detailed explanations on every question.`,
  });

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const clearAll = () => setParams({}, { replace: true });
  const activeFilters = [category && ["category", category], vendor && ["vendor", vendor], difficulty && ["difficulty", difficulty]].filter(Boolean);

  const Filters = (
    <aside className="space-y-7" data-testid="marketplace-filters">
      <div>
        <h3 className="font-display text-sm uppercase tracking-[0.18em] text-muted-foreground mb-3">Category</h3>
        <div className="space-y-1.5">
          {cats.map((c) => (
            <button
              key={c.id}
              onClick={() => update("category", category === c.name ? "" : c.name)}
              className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-sm transition ${
                category === c.name ? "bg-[color:var(--brand-blue)] text-white" : "hover:bg-accent"
              }`}
              data-testid={`filter-cat-${c.slug}`}
            >
              <span>{c.name}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-display text-sm uppercase tracking-[0.18em] text-muted-foreground mb-3">Vendor</h3>
        <div className="space-y-1.5">
          {VENDORS.map((v) => (
            <button
              key={v}
              onClick={() => update("vendor", vendor === v ? "" : v)}
              className={`flex w-full items-center rounded-md px-2.5 py-1.5 text-sm transition ${
                vendor === v ? "bg-[color:var(--brand-blue)] text-white" : "hover:bg-accent"
              }`}
            >{v}</button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-display text-sm uppercase tracking-[0.18em] text-muted-foreground mb-3">Difficulty</h3>
        <div className="flex flex-wrap gap-2">
          {DIFFICULTIES.map((d) => (
            <button
              key={d}
              onClick={() => update("difficulty", difficulty === d ? "" : d)}
              className={`rounded-full px-3 py-1 text-xs font-semibold border transition ${
                difficulty === d ? "bg-foreground text-background border-foreground" : "border-border hover:bg-accent"
              }`}
            >{d}</button>
          ))}
        </div>
      </div>
    </aside>
  );

  return (
    <div data-testid="marketplace-page">
      <section className="border-b border-border bg-[color:var(--brand-cream-2)]">
        <div className="container-x py-12">
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">All practice tests</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            {courses.length} certifications · Updated for 2026 · Detailed explanations on every question
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); update("q", e.target.elements.q.value); }}
            className="mt-6 flex gap-2 max-w-xl"
            data-testid="marketplace-search"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                name="q" defaultValue={q}
                placeholder="Search certifications…"
                className="h-11 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-foreground"
              />
            </div>
            <Button type="submit" className="h-11 bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white">Search</Button>
          </form>
        </div>
      </section>

      <section className="container-x py-10">
        <div className="grid lg:grid-cols-[260px_1fr] gap-10">
          <div className="hidden lg:block">{Filters}</div>

          <div>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <button
                  onClick={() => setShowFilters(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold"
                  data-testid="open-mobile-filters"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
                </button>
                {activeFilters.map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs">
                    {v}
                    <button onClick={() => update(k, "")}><X className="h-3 w-3" /></button>
                  </span>
                ))}
                {activeFilters.length > 0 && (
                  <button onClick={clearAll} className="text-xs underline text-muted-foreground hover:text-foreground">
                    Clear all
                  </button>
                )}
              </div>
              <select
                value={sort}
                onChange={(e) => update("sort", e.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                data-testid="sort-select"
              >
                <option value="popular">Most popular</option>
                <option value="rating">Highest rated</option>
                <option value="newest">Newest</option>
                <option value="price_low">Price: low → high</option>
                <option value="price_high">Price: high → low</option>
              </select>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="rounded-xl border border-border h-72 animate-pulse bg-muted/40" />
                ))}
              </div>
            ) : courses.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-16 text-center">
                <h3 className="font-display text-xl">No certifications match those filters</h3>
                <p className="text-muted-foreground mt-1 text-sm">Try clearing filters or searching for something broader.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="course-grid">
                {courses.map((c) => <CourseCard key={c.id} course={c} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      <RecentlyViewed />

      {/* Mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/50 lg:hidden" onClick={() => setShowFilters(false)}>
          <div className="absolute inset-y-0 left-0 w-[85%] max-w-sm bg-background p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-lg">Filters</h3>
              <button onClick={() => setShowFilters(false)}><X className="h-5 w-5" /></button>
            </div>
            {Filters}
          </div>
        </div>
      )}
    </div>
  );
}
